import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Footer from '../components/Footer';
import { useTheme } from '../context/ThemeContext';

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

const TOPICS = [
  { value: '', label: 'Select a topic' },
  { value: 'problem', label: 'Problem' },
  { value: 'idea', label: 'Idea' },
  { value: 'account-help', label: 'Account help' },
  { value: 'account-deletion', label: 'Account or data deletion' },
  { value: 'other', label: 'Other' },
];

const DELETION_SCOPES = [
  { value: '', label: 'Select what to delete' },
  { value: 'account-and-data', label: 'My account and all associated data' },
  { value: 'data-only', label: 'Specific data only (keep my account)' },
];

const Contact: React.FC = () => {
  const { colors, dsColors } = useTheme();
  const [searchParams] = useSearchParams();
  const platformFromUrl = searchParams.get('platform') || '';

  const [topic, setTopic] = useState('');
  const [deletionScope, setDeletionScope] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [platform, setPlatform] = useState(platformFromUrl);
  const [state, setState] = useState<SubmitState>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (platformFromUrl) setPlatform(platformFromUrl);
  }, [platformFromUrl]);

  const encode = (data: Record<string, string>) =>
    Object.keys(data)
      .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
      .join('&');

  const isDeletionRequest = topic === 'account-deletion';

  const handleTopicChange = (value: string) => {
    setTopic(value);
    if (value !== 'account-deletion') setDeletionScope('');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!topic || !message.trim() || (isDeletionRequest && !deletionScope)) return;

    setState('submitting');
    setErrorMsg('');

    const formData: Record<string, string> = {
      'form-name': 'contact',
      topic,
      message: message.trim(),
    };
    if (email.trim()) formData.email = email.trim();
    if (platform) formData.platform = platform;
    if (isDeletionRequest) formData['deletion-scope'] = deletionScope;

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode(formData),
      });

      if (!response.ok) throw new Error(`Server returned ${response.status}`);

      setState('success');
    } catch (err) {
      setState('error');
      setErrorMsg(
        'Something went wrong sending your message. Please try again, or email us directly at support@ridepulse.app.'
      );
    }
  };

  const resetForm = () => {
    setTopic('');
    setDeletionScope('');
    setMessage('');
    setEmail('');
    setState('idle');
    setErrorMsg('');
  };

  const cardBorder = colors.border;
  const fieldStyle: React.CSSProperties = {
    borderColor: cardBorder,
    color: colors.textPrimary,
  };
  const fieldClass =
    'w-full h-12 rounded-md px-3 text-sm border bg-transparent focus:outline-none focus-visible:ring-2';

  const primaryBtn: React.CSSProperties = {
    backgroundColor: dsColors.primaryStrong,
    color: '#ffffff',
  };
  const quietBtn: React.CSSProperties = {
    backgroundColor: colors.surface,
    color: colors.textPrimary,
    border: `1px solid ${cardBorder}`,
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 md:pt-28">
        {state === 'success' ? (
          <div
            className="max-w-xl mx-auto p-8 sm:p-10 rounded-2xl border text-center"
            style={{ borderColor: cardBorder, backgroundColor: colors.surface }}
          >
            <div
              className="w-14 h-14 mx-auto mb-5 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${dsColors.successStrong}26` }}
            >
              <svg className="w-7 h-7" style={{ color: dsColors.successStrong }} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-3" style={{ color: colors.textPrimary }}>
              Message sent
            </h1>
            <p className="mb-7 text-sm" style={{ color: colors.textSecondary }}>
              Thanks for your feedback. We will get back to you at the email you provided if needed.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/"
                className="inline-flex items-center px-4 py-2 rounded-md text-sm font-semibold transition focus:outline-none focus-visible:ring-2"
                style={{ ...quietBtn, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
              >
                Back to home
              </Link>
              <button
                type="button"
                onClick={resetForm}
                className="inline-flex items-center px-4 py-2 rounded-md text-sm font-semibold shadow-sm hover:shadow-md transition focus:outline-none focus-visible:ring-2"
                style={{ ...primaryBtn, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
              >
                Send another message
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_15rem] gap-10 items-start">
            <div>
              <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-3" style={{ color: dsColors.primary }}>
                Support
              </p>
              <h1 className="text-3xl sm:text-4xl font-bold mb-3 leading-tight" style={{ color: colors.textPrimary, textWrap: 'balance' }}>
                Send feedback
              </h1>
              <p className="text-base sm:text-lg mb-10 max-w-xl" style={{ color: colors.textSecondary, textWrap: 'pretty' }}>
                Found a bug, have an idea, or need help? Send us a short note and we will take it from there.
              </p>

              <div
                className="p-6 sm:p-8 rounded-2xl border shadow-sm"
                style={{ borderColor: cardBorder, backgroundColor: colors.surface }}
              >
                {/* Hidden Netlify form detection field */}
                <form
                  name="contact"
                  method="POST"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <input type="hidden" name="form-name" value="contact" />
                  {platform && <input type="hidden" name="platform" value={platform} />}

                  <p className="hidden">
                    <label>
                      Don&apos;t fill this out:{' '}
                      <input name="bot-field" />
                    </label>
                  </p>

                  {/* Topic */}
                  <div>
                    <label htmlFor="topic" className="block text-sm font-medium mb-1.5" style={{ color: colors.textPrimary }}>
                      Topic <span style={{ color: dsColors.error }}>*</span>
                    </label>
                    <select
                      id="topic"
                      name="topic"
                      required
                      value={topic}
                      onChange={(e) => handleTopicChange(e.target.value)}
                      className={fieldClass}
                      style={fieldStyle}
                    >
                      {TOPICS.map((t) => (
                        <option key={t.value} value={t.value} disabled={t.value === ''}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Deletion scope (only for account/data deletion requests) */}
                  {isDeletionRequest && (
                    <div>
                      <label htmlFor="deletion-scope" className="block text-sm font-medium mb-1.5" style={{ color: colors.textPrimary }}>
                        What should we delete? <span style={{ color: dsColors.error }}>*</span>
                      </label>
                      <select
                        id="deletion-scope"
                        name="deletion-scope"
                        required
                        value={deletionScope}
                        onChange={(e) => setDeletionScope(e.target.value)}
                        className={fieldClass}
                        style={fieldStyle}
                      >
                        {DELETION_SCOPES.map((s) => (
                          <option key={s.value} value={s.value} disabled={s.value === ''}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs" style={{ color: colors.textTertiary }}>
                        Include the email address or username on your account in the message so we can verify the request.
                        You can also delete your account directly in the app: Settings &gt; Account &gt; Delete Account.
                      </p>
                    </div>
                  )}

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-1.5" style={{ color: colors.textPrimary }}>
                      Message <span style={{ color: dsColors.error }}>*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={message}
                      maxLength={10000}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Tell us what you expected and what happened."
                      className="w-full rounded-md px-3 py-2.5 text-sm border bg-transparent resize-y focus:outline-none focus-visible:ring-2"
                      style={fieldStyle}
                    />
                    <p className="mt-1 text-xs" style={{ color: colors.textTertiary }}>
                      A short note is enough. Screenshots, logs, and build numbers are optional.
                    </p>
                  </div>

                  {/* Email (optional) */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-1.5" style={{ color: colors.textPrimary }}>
                      Reply email <span className="text-xs font-normal" style={{ color: colors.textTertiary }}>(optional)</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      maxLength={254}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className={fieldClass}
                      style={fieldStyle}
                    />
                    <p className="mt-1 text-xs" style={{ color: colors.textTertiary }}>
                      Leave it blank if you do not need a reply.
                    </p>
                  </div>

                  {/* Error state */}
                  {state === 'error' && (
                    <div
                      className="p-3 rounded-md text-sm flex items-start gap-2"
                      style={{
                        backgroundColor: `${dsColors.error}1a`,
                        border: `1px solid ${dsColors.error}4d`,
                        color: colors.textPrimary,
                      }}
                    >
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: dsColors.error }} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span>{errorMsg}</span>
                    </div>
                  )}

                  {/* Submit */}
                  <div className="flex items-center justify-between gap-4 pt-1">
                    <p className="text-xs" style={{ color: colors.textTertiary }}>
                      We typically respond within 1 to 3 business days.
                    </p>
                    <button
                      type="submit"
                      disabled={state === 'submitting' || !topic || !message.trim() || (isDeletionRequest && !deletionScope)}
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold shadow-sm hover:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 max-w-full"
                      style={{ ...primaryBtn, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
                    >
                      {state === 'submitting' && (
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      )}
                      {state === 'submitting' ? 'Sending...' : 'Send feedback'}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Other ways — quiet aside */}
            <aside className="space-y-6 md:pt-24">
              <div>
                <h2 className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: colors.textSecondary }}>
                  Guides
                </h2>
                <p className="text-sm mb-2" style={{ color: colors.textTertiary }}>
                  Step-by-step help for every feature.
                </p>
                <Link to="/help" className="text-sm font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded" style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}>
                  Browse Help →
                </Link>
              </div>
              <div>
                <h2 className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: colors.textSecondary }}>
                  Direct
                </h2>
                <p className="text-sm mb-2" style={{ color: colors.textTertiary }}>
                  Prefer email?
                </p>
                <a href="mailto:support@ridepulse.app" className="text-sm font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded" style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}>
                  support@ridepulse.app
                </a>
              </div>
              <div>
                <h2 className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: colors.textSecondary }}>
                  Status
                </h2>
                <Link to="/status" className="text-sm font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded" style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}>
                  Check service status →
                </Link>
              </div>
            </aside>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
